/** Default side-panel size (percentage) for every tab but Summary, also the fallback when restoring from maximized. */
export const DEFAULT_PANEL_SIZE_PERCENT = 50;

/** Minimum size (in pixels) the side panel may shrink to. */
export const PANEL_MIN_PIXELS = 320;

/** Minimum size (in pixels) the main content area must keep. */
export const PANEL_MAIN_MIN_PIXELS = 360;

/**
 * The widest a docked panel gets: what the conversation leaves beside it, at its minimum width
 * (`cqw`: the workspace's width). However narrow the window, the panel never covers it.
 */
export const DOCKED_PANEL_MAX_WIDTH = `max(0px, calc(100cqw - ${PANEL_MAIN_MIN_PIXELS}px))`;

/** Percentage step applied for a single keyboard resize press. */
const KEYBOARD_RESIZE_STEP = 2;

/** Smallest side-panel size (percentage) reachable via keyboard resize. */
export const PANEL_SIZE_MIN_PERCENT = 20;

/** Largest side-panel size (percentage) reachable via keyboard resize. */
export const PANEL_SIZE_MAX_PERCENT = 90;

/**
 * Clamps a pointer-driven resize to the pixel minimums for both the panel and
 * the main content area, expressed as a percentage of the container dimension.
 */
export function clampPointerResize(params: {
  nextSize: number;
  dimension: number;
  minimumPixels: number;
  mainMinimumPixels: number;
}): number {
  const { nextSize, dimension, minimumPixels, mainMinimumPixels } = params;
  const minimum = Math.min((minimumPixels / dimension) * 100, 100);
  const maximum = Math.max(minimum, 100 - (mainMinimumPixels / dimension) * 100);
  return Math.min(maximum, Math.max(minimum, nextSize));
}

/** Applies one keyboard resize step and clamps to the percentage bounds. */
export function clampKeyboardResize(current: number, direction: 'increase' | 'decrease'): number {
  const step = direction === 'increase' ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP;
  return Math.min(PANEL_SIZE_MAX_PERCENT, Math.max(PANEL_SIZE_MIN_PERCENT, current + step));
}
